import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type NumberDetail,
  type NumberPlan,
  type PurchaseIntent
} from "../api";
import { useApiData } from "../hooks";
import { useApp } from "../store";
import {
  Empty,
  ErrorNote,
  Header,
  Loading,
  Note,
  SectionLabel,
  StateBadge,
  formatPrice,
  formatWhen
} from "../ui";

/** Owned numbers grouped by state, plus the entry point to buying one. */
export function NumbersPage(): JSX.Element {
  const { numbers, refreshNumbers } = useApp();
  const { data: intents, reload: reloadIntents } = useApiData<PurchaseIntent[]>("/api/v1/numbers/purchases");
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<unknown>(null);
  const active = numbers.filter((number) => number.state === "ACTIVE");
  const disabled = numbers.filter((number) => number.state === "DISABLED");
  const released = numbers.filter((number) => number.state === "RELEASED");
  const pending = (intents ?? []).filter((intent) => intent.status === "PENDING_PAYMENT");
  const failed = (intents ?? []).filter((intent) => intent.status === "PROVISION_FAILED");

  async function retryProvision(intentId: string): Promise<void> {
    setRetrying(intentId);
    setRetryError(null);
    try {
      await api.post(`/api/v1/numbers/purchases/${intentId}/retry`);
      await refreshNumbers();
      reloadIntents();
    } catch (caught) {
      setRetryError(caught);
    } finally {
      setRetrying(null);
    }
  }

  return (
    <>
      <Header
        title="My numbers"
        action={
          <Link className="action" to="/numbers/buy" title="Buy number">
            +
          </Link>
        }
      />
      <div className="content">
        {numbers.length === 0 && pending.length === 0 ? (
          <Empty>No numbers yet. Buy one to start calling and messaging.</Empty>
        ) : null}

        <NumberGroup title="Active" numbers={active} />
        <NumberGroup title="Disabled (grace period)" numbers={disabled} />
        <NumberGroup title="Released" numbers={released} />

        {pending.length > 0 ? (
          <>
            <SectionLabel>Awaiting payment</SectionLabel>
            <ul className="list">
              {pending.map((intent) => (
                <li key={intent.intentId}>
                  <div className="row-main">
                    <div className="row-title">{intent.productName}</div>
                    <div className="row-sub">
                      Number is revealed after payment confirms {formatWhen(intent.createdAt)}
                    </div>
                  </div>
                  {intent.checkoutUrl ? (
                    <a className="back" href={intent.checkoutUrl}>
                      Pay
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {failed.length > 0 ? (
          <>
            <SectionLabel>Needs attention</SectionLabel>
            <ErrorNote error={retryError} />
            <ul className="list">
              {failed.map((intent) => (
                <li key={intent.intentId}>
                  <div className="row-main">
                    <div className="row-title">{intent.productName}</div>
                    <div className="row-sub">Paid, but no number was provisioned</div>
                    <div className="row-sub">{intent.failureReason}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => retryProvision(intent.intentId)}
                    disabled={retrying !== null}
                  >
                    {retrying === intent.intentId ? "Retrying..." : "Retry"}
                  </button>
                </li>
              ))}
            </ul>
            <Note>
              Retry to provision again. If the problem persists, an administrator can refund or retry manually.
            </Note>
          </>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <Link className="btn primary" to="/numbers/buy">
            Buy a new number
          </Link>
          <button className="btn secondary" type="button" onClick={() => refreshNumbers()}>
            Refresh
          </button>
        </div>
      </div>
    </>
  );
}

function NumberGroup({ title, numbers }: { title: string; numbers: ReturnType<typeof useApp>["numbers"] }) {
  if (numbers.length === 0) {
    return null;
  }
  return (
    <>
      <SectionLabel>{title}</SectionLabel>
      <ul className="list">
        {numbers.map((number) => (
          <li key={number.id}>
            <div className="avatar sq">{number.countryCode}</div>
            <div className="row-main">
              <div className="row-title">
                <Link className="back" to={`/numbers/${number.id}`}>
                  {number.e164}
                </Link>
              </div>
              <div className="row-sub">
                {number.state === "ACTIVE"
                  ? `${number.devicesOnline} of ${number.devicesRegistered} devices online`
                  : number.state === "DISABLED"
                    ? "Payment failed"
                    : "Returned to the provider"}
              </div>
            </div>
            <StateBadge state={number.state} />
          </li>
        ))}
      </ul>
    </>
  );
}

/** Per-number detail: state, fair-use usage, registered devices, integrations, release. */
export function NumberDetailPage(): JSX.Element {
  const { numberId = "" } = useParams();
  const { refreshNumbers } = useApp();
  const { data, error, reload } = useApiData<NumberDetail>(`/api/v1/numbers/${numberId}`);
  const [actionError, setActionError] = useState<unknown>(null);

  async function release(): Promise<void> {
    setActionError(null);
    try {
      await api.delete(`/api/v1/numbers/${numberId}`);
      await refreshNumbers();
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  if (error) {
    return (
      <>
        <Header back="/numbers" backLabel={"\u2190 Numbers"} />
        <div className="content">
          <ErrorNote error={error} />
        </div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <Header back="/numbers" backLabel={"\u2190 Numbers"} />
        <div className="content">
          <Loading />
        </div>
      </>
    );
  }

  const { number, usage, devices, deviceCap } = data;
  const disabled = number.state === "DISABLED";
  const released = number.state === "RELEASED";

  return (
    <>
      <Header back="/numbers" backLabel={"\u2190 Numbers"} />
      <div className="content">
        <div className="card card-row" style={{ padding: 16 }}>
          <div className="row-main">
            <div className="row-title">{number.e164}</div>
            <div className="row-sub">
              {disabled
                ? "Cannot send or receive while disabled"
                : released
                  ? "Released and returned to the provider."
                  : `${number.devicesOnline} of ${number.devicesRegistered} devices online`}
            </div>
            {released ? <div className="row-sub">This is permanent and cannot be recovered.</div> : null}
            {!released ? (
              <div className="row-sub">
                {formatPrice(number.monthlyAmountMinor, number.currency)}
                {number.subscription?.currentTermEnd
                  ? ` \u00B7 renews ${new Date(number.subscription.currentTermEnd).toLocaleDateString()}`
                  : ""}
              </div>
            ) : null}
          </div>
          <StateBadge state={number.state} />
        </div>

        {disabled ? (
          <>
            <Note>
              This number is in the grace period
              {number.subscription?.graceExpiresAt
                ? ` until ${new Date(number.subscription.graceExpiresAt).toLocaleString()}`
                : ""}
              . Settle payment to reactivate it before it is released.
            </Note>
            <Link className="btn primary" to="/payments">
              Reactivate {"\u2014"} settle payment
            </Link>
          </>
        ) : null}

        <SectionLabel>Usage (fair-use cap)</SectionLabel>
        <div className="card" style={{ padding: 14 }}>
          <div className="row-sub">
            Calls: {usage.calls} / {usage.callCap} in {usage.period}
          </div>
          <div className="row-sub">
            Messages: {usage.messages} / {usage.messageCap} in {usage.period}
          </div>
        </div>

        <SectionLabel>
          Devices ({devices.length} of {deviceCap} registered)
        </SectionLabel>
        {devices.length === 0 ? (
          <Note>No devices registered for this number yet.</Note>
        ) : (
          <ul className="list">
            {devices.map((device) => (
              <li key={device.deviceUuid}>
                <div className="avatar sq">{"\u{1F4F1}"}</div>
                <div className="row-main">
                  <div className="row-title">{device.displayName}</div>
                  <div className="row-sub">
                    {released || disabled
                      ? "Disconnected \u2014 number not active"
                      : device.online
                        ? "Online"
                        : `Offline${device.lastSeenAt ? ` \u2014 last seen ${formatWhen(device.lastSeenAt)}` : ""}`}
                  </div>
                </div>
                <span className={device.online && !disabled && !released ? "dot on" : "dot off"} />
              </li>
            ))}
          </ul>
        )}
        <Note>Up to {deviceCap} devices can be registered per number.</Note>

        {!released ? (
          <>
            <SectionLabel>Manage</SectionLabel>
            <ul className="list">
              <li>
                <div className="row-main">
                  <div className="row-title">
                    <Link className="back" to={`/integrations?numberId=${number.id}`}>
                      Integrations for this number
                    </Link>
                  </div>
                </div>
              </li>
              <li>
                <div className="row-main">
                  <div className="row-title">
                    <Link className="back" to="/payments">
                      Subscription &amp; billing
                    </Link>
                  </div>
                </div>
              </li>
            </ul>
          </>
        ) : null}

        <ErrorNote error={actionError} />
        {!released ? (
          <>
            <Note>
              Releasing is permanent. The number is returned to the provider and cannot be recovered.
            </Note>
            <button className="btn danger" type="button" onClick={release}>
              Release this number
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}

/**
 * Buy a number: choose a plan, pay, and only then is a number provisioned and revealed.
 * No concrete number is shown before payment, because none is reserved.
 */
export function BuyNumberPage(): JSX.Element {
  const { data: plans, error } = useApiData<NumberPlan[]>("/api/v1/numbers/plans");
  const [busy, setBusy] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<unknown>(null);

  async function buy(productId: string): Promise<void> {
    setBusy(productId);
    setBuyError(null);
    try {
      const intent = await api.post<PurchaseIntent>("/api/v1/numbers/purchases", { productId });
      if (intent.checkoutUrl) {
        window.location.href = intent.checkoutUrl;
      }
    } catch (caught) {
      setBuyError(caught);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Header title="Buy a number" back="/numbers" backLabel={"\u2190 Numbers"} />
      <div className="content">
        <Note>
          Choose what kind of number you want. You pay first; the number is provisioned and revealed once
          the payment is confirmed, so nothing is reserved in advance.
        </Note>
        <ErrorNote error={error} />
        <ErrorNote error={buyError} />
        <SectionLabel>Available plans</SectionLabel>
        {plans === null ? (
          <Loading />
        ) : (
          <ul className="list">
            {plans.map((plan) => (
              <li key={plan.id}>
                <div className="avatar sq">{plan.countryCode}</div>
                <div className="row-main">
                  <div className="row-title">{plan.displayName}</div>
                  <div className="row-sub">
                    {formatPrice(plan.monthlyAmountMinor, plan.currency)} {"\u00B7"} {plan.phoneNumberType}
                  </div>
                  <div className="row-sub">
                    Fair use: {plan.fairUseCalls} calls, {plan.fairUseMessages} messages per month
                  </div>
                </div>
                <div className="row-actions">
                  <button type="button" onClick={() => buy(plan.id)} disabled={busy !== null}>
                    {busy === plan.id ? "Starting..." : "Buy"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
